import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { emailService } from '../../services/emailService';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { ALL_CITIES, getDistrictForCity } from '../../utils/locations';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const libraries = ['places'];

export default function CustomerOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [address, setAddress] = useState('');
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });
  const [autocomplete, setAutocomplete] = useState(null);

  const filteredCities = ALL_CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUser(user);

      const { data } = await supabase.from('profiles_1740423033529').select('*').eq('id', user.id).single();
      if (data) {
        setProfile(data);
        if (data.phone) setPhone(data.phone);
        if (data.city) {
          setCity(data.city);
          setCitySearch(data.city);
        }
        if (data.address) setAddress(data.address);
      }
    };
    fetchUser();
  }, [navigate]);

  const handleComplete = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 9 || !city) {
      showToast("Please complete the required fields.", "error");
      return;
    }

    setLoading(true);
    try {
      const district = getDistrictForCity(city);
      const { error } = await supabase
        .from('profiles_1740423033529')
        .update({
          phone,
          city,
          district,
          address,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      if (profile && !profile.welcome_sent) {
        await emailService.sendWelcomeEmail(profile.full_name, 'customer', profile.email);
        await supabase.from('profiles_1740423033529').update({ welcome_sent: true }).eq('id', user.id);
      }

      navigate('/app');
    } catch (err) {
      showToast('Error saving profile: ' + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToBarber = () => {
    setShowSwitchModal(true);
  };

  const executeSwitchToBarber = async () => {
    setLoading(true);
    try {
      await supabase.from('profiles_1740423033529').update({ role: 'barber' }).eq('id', user.id);
      navigate('/barber/onboarding');
    } catch (err) {
      showToast("Error switching account type.", "error");
      setLoading(false);
      setShowSwitchModal(false);
    }
  };

  const onLoadGoogleMaps = (autoC) => setAutocomplete(autoC);
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.formatted_address) setAddress(place.formatted_address);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <div className="w-full max-w-md bg-white shadow-2xl rounded-[2.5rem] overflow-hidden flex flex-col animate-in zoom-in-95">
        {/* Banner for switching roles */}
        <div className="w-full bg-brand-dark p-4 text-center border-b border-white/10 flex justify-between items-center px-8 flex-shrink-0 shadow-inner">
          <span className="text-xs font-black text-gray-300 uppercase tracking-wider">Are you a Barber?</span>
          <button 
            type="button" 
            onClick={handleSwitchToBarber} 
            disabled={loading}
            className="text-xs font-black text-brand-accent bg-white/10 px-4 py-2 rounded-full hover:bg-white hover:text-brand-dark uppercase tracking-widest transition-all disabled:opacity-50"
          >
            Switch Role
          </button>
        </div>

        <div className="p-8 text-center">
          <h2 className="text-2xl font-black text-brand-dark mb-8">Setup Your Profile as a Customer</h2>

          <form onSubmit={handleComplete} className="space-y-5 text-left">
            {/* Phone Input */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Phone Number *</label>
              <div className="flex bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-brand-primary overflow-visible transition-colors relative z-50">
                <PhoneInput
                  defaultCountry="lk"
                  value={phone}
                  onChange={(p) => setPhone(p)}
                  className="w-full flex"
                  inputStyle={{ width: '100%', height: '56px', border: 'none', backgroundColor: 'transparent', fontWeight: '700', fontSize: '1rem', outline: 'none', paddingLeft: '12px' }}
                  countrySelectorStyleProps={{ buttonStyle: { height: '56px', border: 'none', backgroundColor: 'transparent', paddingLeft: '16px', paddingRight: '8px' } }}
                />
              </div>
            </div>

            {/* City Dropdown */}
            <div className="relative">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Your City *</label>
              <div className="relative">
                <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <input 
                  type="text" 
                  placeholder="Search your city..."
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(e.target.value);
                    setCity('');
                    setShowCityDropdown(true);
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  className="w-full pl-12 pr-10 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                />
                {city && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <SafeIcon name="CheckCircle" className="text-green-500" />
                  </div>
                )}
              </div>

              {showCityDropdown && !city && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowCityDropdown(false)}></div>
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-60 overflow-y-auto py-2 text-left">
                    {filteredCities.length > 0 ? (
                      filteredCities.map(c => (
                        <div 
                          key={c} 
                          className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors flex justify-between items-center"
                          onClick={() => {
                            setCity(c);
                            setCitySearch(c);
                            setShowCityDropdown(false);
                          }}
                        >
                          <span>{c}</span>
                          <span className="text-[10px] text-gray-400 font-medium">{getDistrictForCity(c)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="px-5 py-3 text-gray-400 text-sm italic">No cities found</div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Address Autocomplete */}
            {isLoaded && (
              <div className="relative">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Default Service Address (Optional)</label>
                <Autocomplete onLoad={onLoadGoogleMaps} onPlaceChanged={onPlaceChanged}>
                  <input 
                    type="text" 
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-5 py-4 border border-brand-primary/20 rounded-2xl bg-blue-50/30 outline-none focus:border-brand-primary font-bold pr-12"
                    placeholder="Home address..."
                  />
                </Autocomplete>
                <div className="absolute right-4 top-[42px] text-brand-primary"><SafeIcon name="MapPin" /></div>
              </div>
            )}

            <div className="pt-4">
              <Button type="submit" className="w-full py-4 text-lg font-black" disabled={loading}>
                {loading ? 'Saving...' : 'Finish Setup'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Switch to Barber Modal */}
      {showSwitchModal && (
        <div className="fixed inset-0 bg-brand-dark/80 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="Scissors" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2">Switch to Barber?</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 leading-relaxed">
              Are you sure you want to switch from <strong>Customer</strong> to <strong>Barber</strong>?
            </p>
            <div className="flex flex-col gap-3">
              <Button className="w-full py-4 font-black" onClick={executeSwitchToBarber} disabled={loading}>
                {loading ? 'Switching...' : 'Yes, I am a Barber'}
              </Button>
              <Button variant="ghost" className="w-full py-3 text-gray-500 font-bold" onClick={() => setShowSwitchModal(false)} disabled={loading}>
                No, Stay as Customer
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}